import { useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import { Link as RouterLink, useParams } from 'react-router-dom'
import AddVendorContactDialog from 'vendors/components/vendorContact/dialogs/AddVendorContactDialog'
import VendorContactHeader from 'vendors/components/vendorContact/VendorContactHeader'
import VendorContactCards from 'vendors/components/vendorContact/VendorContactCards'
import EditVendorGeneralContactDialog from 'vendors/components/vendorContact/dialogs/EditVendorGeneralContactDialog'
import DeleteUserContactDialog from 'vendors/components/vendorContact/dialogs/deleteUserContactDialog/DeleteUserContactDialog'
import DeleteGeneralContactDialog from 'vendors/components/vendorContact/dialogs/DeleteGeneralContactDialog'
import EditVendorUserContactDialog from 'vendors/components/vendorContact/dialogs/EditVendorUserContactDialog'
import { useTranslation } from 'react-i18next'
import { useGetVendorContactsById } from 'vendors/queries/useVendorQuery'

type DeleteContactType = {
  contactId: string
  contactEmail: string
}

const useVendorId = () => {
  const { vendorId } = useParams<{ vendorId: string }>()
  return vendorId as string 
}

const VendorContactInfoStep: React.FC<{ baseUrl: string }> = ({ baseUrl }) => {
  const { t } = useTranslation()
  const [isOpen, setIsOpen] = useState(false)
  const [editVendorUserContact, setIsEditVendorUserContact] = useState<string>('')
  const [editVendorGeneralContact, setIsEditVendorGeneralContact] = useState<string>('')
  const [deleteUserContact, setDeleteUserContact] = useState<DeleteContactType | null>(null)
  const [deleteGeneralContact, setDeleteGeneralContact] = useState<DeleteContactType | null>(null)

  const vendorId = useVendorId() 

  const { data: contacts, isLoading: isContactsLoading } = useGetVendorContactsById(vendorId)

  const handleOpenAddVendorContactDialog = () => {
    setIsOpen(true)
  }

  const handleOpenEditVendorContactDialog = (userContactId: string) => {
    setIsEditVendorUserContact(userContactId)
  }

  const handleOpenEditGeneralContactDialog = (generalContactId: string) => {
    setIsEditVendorGeneralContact(generalContactId)
  }

  const handleOpenDeleteUserContactDialog = (contactId: string, contactEmail: string) => {
    setDeleteUserContact({ contactId, contactEmail })
  }

  const handleOpenDeleteGeneralContactDialog = (contactId: string, contactEmail: string) => {
    setDeleteGeneralContact({ contactId, contactEmail })
  }

  return (
    <>
      <Box sx={{ px: 2, pb: 2 }}>
        <Box>
          <VendorContactHeader />
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
            <Button variant="outlined" color="grey" onClick={handleOpenAddVendorContactDialog}>
              {t('add')}
            </Button>
          </Box>
        </Box>
        <VendorContactCards
          contacts={contacts}
          isContactsLoading={isContactsLoading}
          vendorId={vendorId}
          onVendorContactEdit={handleOpenEditVendorContactDialog}
          onVendorGeneralContactEdit={handleOpenEditGeneralContactDialog}
          handleOpenDeleteUserContactDialog={handleOpenDeleteUserContactDialog}
          handleOpenDeleteGeneralContactDialog={handleOpenDeleteGeneralContactDialog}
        />
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-end'
          }}
        >
          <Button sx={{ mt: 3 }} component={RouterLink} to={`${baseUrl}/${vendorId}/services`} variant="contained">
            {t('next')}
          </Button>
        </Box>
        <Typography sx={{ fontStyle: 'italic', mt: 3 }} variant="body2" color="textSecondary">
          {t('contacts-helper-text')}
        </Typography>
      </Box>
      {isOpen && (
        <AddVendorContactDialog
          contacts={contacts}
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          vendorId={vendorId}
        />
      )}
      {editVendorUserContact && (
        <EditVendorUserContactDialog
          onClose={() => setIsEditVendorUserContact('')}
          vendorId={vendorId}
          userContactId={editVendorUserContact}
        />
      )}
      {editVendorGeneralContact && (
        <EditVendorGeneralContactDialog
          onClose={() => setIsEditVendorGeneralContact('')}
          vendorId={vendorId}
          generalContactId={editVendorGeneralContact}
        />
      )}
      {deleteUserContact && (
        <DeleteUserContactDialog
          isOpen={deleteUserContact !== null}
          onClose={() => setDeleteUserContact(null)}
          vendorId={vendorId}
          {...deleteUserContact}
        />
      )}
      {deleteGeneralContact && (
        <DeleteGeneralContactDialog
          isOpen={deleteGeneralContact !== null}
          onClose={() => setDeleteGeneralContact(null)}
          vendorId={vendorId}
          {...deleteGeneralContact}
        />
      )}
    </>
  )
}

export default VendorContactInfoStep
